import { useCallback, useRef, useState } from 'react'
import { Box, TextField, Typography } from '@mui/material'
import { STEPS } from '../onboardingSteps'
import { useOnboardingV2 } from '../OnboardingContext'
import { useWizardData } from '../hooks/useWizardData'
import { useStep } from '../hooks/useStep'
import { COLORS } from '../theme'
import {
  stepHeadingSx,
  stepDescriptionSx,
  inputSx,
  textFieldSx,
} from '../styles'
import SpeechToText from '@/components/SpeechToText'
import { getCurrentUser } from '@/utils/auth/UserProvider'

export default function UploadAnythingElse() {
  const { token } = getCurrentUser()

  const { maxStepReached, setStaleFrom } = useOnboardingV2()
  const [wizardData, setWizardData] = useWizardData()

  const initialContextRef = useRef(wizardData.additionalContext)

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setWizardData({ additionalContext: e.target.value })
    },
    [setWizardData],
  )

  useStep({
    isValid: true,
    beforeNext: async () => {
      if (
        wizardData.additionalContext !== initialContextRef.current &&
        maxStepReached >= STEPS.PROFILE_GENERATING
      ) {
        setStaleFrom((prev) =>
          prev === null
            ? STEPS.PROFILE_GENERATING
            : Math.min(prev, STEPS.PROFILE_GENERATING),
        )
      }
    },
  })

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography variant="h5" sx={stepHeadingSx}>
        Anything else to add?
      </Typography>
      <Typography variant="body1" sx={{ ...stepDescriptionSx, mb: 2 }}>
        Add any info that is not already contained in the websites and documents
        you uploaded in the previous steps. You don&apos;t need to re-type
        anything that&apos;s already in those resources. Fundsorter will combine
        everything together to generate your profile.
      </Typography>
      {token && (
        <SpeechToText
          token={token}
          onTranscription={(text) =>
            setWizardData((prev) => ({
              ...prev,
              additionalContext: prev.additionalContext
                ? `${prev.additionalContext}\n\n${text}`
                : text,
            }))
          }
        />
      )}

      <TextField
        fullWidth
        multiline
        minRows={8}
        placeholder="e.g. We recently expanded our youth mentoring programme..."
        value={wizardData.additionalContext}
        onChange={handleChange}
        sx={textFieldSx}
        slotProps={{ input: { sx: inputSx } }}
      />
      <Typography variant="caption" sx={{ color: COLORS.secondary, mt: 0.5 }}>
        Optional
      </Typography>
    </Box>
  )
}
